import React from "react";
import { filter, map } from "lodash";
import { FaTrash } from "react-icons/fa";
import { MdOutlineAdd } from "react-icons/md";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";

import { ConnectForm, URLRegex } from "@/utils/formHelper";

import { Typography } from "@/components/Typography";
import { ImageUpload } from "@/components/ImageUpload";
import { InputAdapter } from "@/components/Input/adapter/InputAdapter";

import { FormValidationMethods } from "@/components/Form/types";
import { LinkInterface, LinksProps } from "./types";

export const LINK_ID_PREFIX = "link";
export const MAX_LINKS = 10;

import "./styles.scss";

export const LinksLib = (props: LinksProps) => {
  const methods: FormValidationMethods = useFormContext();
  const { t } = useTranslation("listoflinks");

  const {
    title,
    titleEmpty,
    defaultValue,
    linkLabel,
    linkPlaceHolder,
    linkUrlLabel,
    linkUrlPlaceHolder,
    linksButtonAddLink,
    invalidLinkUrl,
    onClick,
  } = props;

  const [addedLinks, setAddedLinks] = React.useState<LinkInterface[] | undefined>(
    defaultValue || []
  );

  const linkId = (index: number | string) => `${LINK_ID_PREFIX}-${index}`;

  const addNewLink = () => {
    const newLink: LinkInterface = {
      id: linkId(addedLinks.length),
      linkImageUrl: "",
      linkText: "",
      linkUrl: "",
    };

    setAddedLinks([...addedLinks, newLink]);
  };

  const removeLink = (id: string | number) => {
    if (!methods) return;

    const currentLinks = [...addedLinks];
    const removedIndex = currentLinks.findIndex((link) => link.id === id);

    if (removedIndex === -1) return;

    for (let i = removedIndex; i < currentLinks.length; i++) {
      methods.unregister(`text-link-${i}`);
      methods.unregister(`url-link-${i}`);
      methods.unregister(`image-${i}`);

      methods.clearErrors(`text-link-${i}`);
      methods.clearErrors(`url-link-${i}`);
      methods.clearErrors(`image-${i}`);
    }

    let newLinks = filter(currentLinks, (link) => link.id !== id);

    newLinks = map(newLinks, (link, idx) => ({
      ...link,
      id: linkId(idx),
    }));

    setAddedLinks(newLinks);

    setTimeout(() => {
      newLinks.forEach((link, idx) => {
        if (link.linkText) {
          methods.setValue(`text-link-${idx}`, link.linkText);
        }
        if (link.linkUrl) {
          methods.setValue(`url-link-${idx}`, link.linkUrl);
        }
        if (link.linkImageUrl) {
          methods.setValue(`image-${idx}`, link.linkImageUrl);
        }
      });
    }, 0);
  };

  const onImageLoad = (image: string, index: string | number) => {
    setAddedLinks(
      map(addedLinks, (link) => {
        return link.id === linkId(index) ? { ...link, linkImageUrl: image[0] } : link;
      })
    );
  };

  const onHandleInput = (name: string, inputType: "text" | "url") => {
    const value = methods?.getValues(name);
    setAddedLinks(
      map(addedLinks, (link) =>
        `${inputType}-${link.id}` === name
          ? inputType === "text"
            ? { ...link, linkText: value }
            : { ...link, linkUrl: value }
          : link
      )
    );
  };

  const renderLinkMessage = () => {
    return addedLinks.length !== MAX_LINKS ? (
      addedLinks.length ? (
        <Typography
          classname="u-bold"
          Tag={"p"}
        >
          {titleEmpty}
        </Typography>
      ) : (
        <React.Fragment>
          <Typography
            classname="u-bold"
            Tag={"p"}
          >
            {title}
            <em
              className={methods?.formState.errors["emptyListOfLinks"] ? "Links__error" : undefined}
            >
              *
            </em>
          </Typography>
          {methods?.formState.errors["emptyListOfLinks"] && (
            <p className="Links__error">
              {methods?.formState.errors["emptyListOfLinks"].message as string}
            </p>
          )}
        </React.Fragment>
      )
    ) : undefined;
  };

  React.useEffect(() => {
    onClick && onClick(addedLinks);
  }, [addedLinks]);

  React.useEffect(() => {
    if (methods?.formState.isSubmitting) {
      if (addedLinks.length === 0) {
        methods?.setError("emptyListOfLinks", {
          type: "required",
          message: t("empty_list_of_links"),
        });
      } else {
        methods?.clearErrors("emptyListOfLinks");
      }
    }
  }, [methods?.formState]);

  return (
    <ConnectForm>
      <div
        data-testid="Links"
        data-qa={props.dataQA}
        className="Links"
      >
        {addedLinks.map((item, index) => {
          return (
            <div
              className={`Links__input-wrapper`}
              key={`${item.id}-${index}`}
            >
              {" "}
              <div className={`Links__header`}>
                <Typography
                  Tag={"p"}
                  classname="u-bold"
                >
                  {`Link ${index + 1}`}
                </Typography>
                <ButtonLib
                  variant="error"
                  iconLeft={<FaTrash />}
                  shape="outlined"
                  onClick={() => removeLink(item.id)}
                  ariaLabel="delete"
                  dataQA="delete-link"
                />
              </div>
              <ImageUpload
                title="Image"
                id={`image-${index}`}
                onClick={(image) => onImageLoad(image, index)}
                defaultValue={item.linkImageUrl}
              />
              <InputAdapter
                id={`text-link-${index}`}
                type="text"
                dataQA={`text-link-${index}`}
                name={`text-link-${index}`}
                label={linkLabel}
                placeholder={linkPlaceHolder}
                defaultValue={item.linkText}
                onChange={() => onHandleInput(`text-link-${index}`, "text")}
                rules={{
                  required: {
                    value: true,
                    message: t("required_message"),
                  },
                  maxLength: {
                    value: 50,
                    message: t("links_max_length"),
                  },
                  pattern: {
                    value: new RegExp("^\\S.*$[ A-Za-z0-9_@./#&+,-]*$"),
                    message: t("one_char"),
                  },
                }}
              />
              <InputAdapter
                id={`url-link-${index}`}
                type="text"
                dataQA={`url-link-${index}`}
                name={`url-link-${index}`}
                label={linkUrlLabel}
                placeholder={linkUrlPlaceHolder}
                defaultValue={item.linkUrl}
                onChange={() => onHandleInput(`url-link-${index}`, "url")}
                rules={{
                  required: {
                    value: true,
                    message: t("required_message"),
                  },
                  pattern: {
                    value: new RegExp(URLRegex),
                    message: invalidLinkUrl,
                  },
                }}
              />
            </div>
          );
        })}
        {renderLinkMessage()}
        <ButtonLib
          label={linksButtonAddLink}
          onClick={addNewLink}
          disabled={addedLinks.length === MAX_LINKS}
          id="add-link-button"
          iconLeft={<MdOutlineAdd />}
          shape="ghost"
          dataQA="add-link"
        />
      </div>
    </ConnectForm>
  );
};
